import React, { useContext, useMemo, useCallback } from 'react';
import { capitalizeAll, SlideOutComp, SlideOutCompProps } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { Alert } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { AlertInfoContainer } from './AlertInfoContainer';
import { useSelectAlert } from '../../utils/dataStateHooks';
import { RouteHelper } from '..';



interface AlertPreviewContainerProps {
    readonly projectId: number;
    readonly alertId: string;

    // To allow the parent container to handle onShowEntityDetails event instead of this component
    readonly onShowEntityDetailsHandler?: ((entityId: string)=> void)|undefined;
}


/**
 * This Component is used for displaying an Alert in the preview panel for preview
 */
export const AlertPreviewContainer: React.FC<AlertPreviewContainerProps> = (props: AlertPreviewContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);


    // Get the alert object for the specified alertId
    const selectedAlert = useSelectAlert(props.projectId, props.alertId);



    /**
     * On Investigate Alert button clicked button clicked, take the user to the investigate alert page
     */
    const onInvestigateAlert = useCallback((alert: Alert) => {
        RouteHelper.getInstance().gotoInvestigateAlertPage(props.projectId, alert.id);
    }, [props]);



    /**
     * On Show Entity Details button clicked
     */
    const onShowEntityDetails = useCallback((entityId: string) => {
        // If onEntitySelect handler is provided then call the handler.
        if (props.onShowEntityDetailsHandler) {
            props.onShowEntityDetailsHandler(entityId);
        } else {
            RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entityId);
        }
    }, [props]);



    /**
     * Create an Array of primary actions for the Alert
     */
    const primaryActions = useMemo((): SlideOutCompProps['primaryActions'] => {
        if (selectedAlert) {
            return [{
                label  : capitalizeAll(trans('label.investigate_alert')),
                onClick: () => {
                    onInvestigateAlert(selectedAlert);
                },
            }, {
                label  : capitalizeAll(trans('entity.action.show_entity_details', { term: trans('terms.entity') })),
                onClick: () => {
                    onShowEntityDetails(selectedAlert.entityId);
                },
            }];
        }

        return undefined;
    }, [onInvestigateAlert, onShowEntityDetails, selectedAlert, trans]);


    /**
     * Create secondary actions for the Alert
     */
    const secondaryActions = useMemo(() => {
        return undefined;
    }, []);



    /**
     * Create the content for the slide-out panel. The content will just going to be a Entity
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <AlertInfoContainer
                className="transparent"
                projectId={props.projectId}
                alertId={props.alertId}
            />
        );
    }, [props.projectId, props.alertId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable
        />
    );
};
